import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { School, Loader2, ShieldAlert, Phone } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { usePageView } from '../hooks/usePageView';
import api from '../lib/api';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

export default function LoginPage() {
  usePageView('/login');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isLoading, error, errorCode, clearError } = useAuthStore();

  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // Fetch available schools
  useEffect(() => {
    api.get('/auth/tenants')
      .then((res) => {
        const list = res?.data || [];
        setTenants(list);
        // Auto-select if only one tenant
        if (list.length === 1) setSelectedTenant(String(list[0].id));
      })
      .catch(() => setTenants([]))
      .finally(() => setLoadingTenants(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    try {
      const res = await login(username, password, selectedTenant || null);
      const userRole = res?.data?.user?.role || 'admin';
      const landingPages = {
        admin: '/dashboard',
        accountant: '/dashboard',
        principal: '/dashboard',
        teacher: '/students/grades',
        hr: '/hr/employees',
      };
      navigate(landingPages[userRole] || '/dashboard', { replace: true });
    } catch {
      // Error is set in the store
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">
          {t('auth.welcomeBack')}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {t('auth.loginSubtitle')}
        </p>
      </div>

      {error && errorCode === 'AUTH_004' ? (
        <div className="mb-4 p-4 rounded-2xl bg-amber-50 border border-amber-200">
          <div className="flex items-start gap-3">
            <ShieldAlert size={20} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">{t('auth.accountSuspended')}</p>
              <p className="text-xs text-amber-700 mt-1">{t('auth.accountSuspendedDesc')}</p>
              <a href="https://wa.me/962791234567" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800 text-xs font-semibold hover:bg-amber-200 transition-colors">
                <Phone size={12} />
                {t('auth.contactSupport')}
              </a>
            </div>
          </div>
        </div>
      ) : error ? (
        <div className="mb-4 p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {t('auth.loginError')}
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* School selector */}
        {loadingTenants ? (
          <div className="flex items-center justify-center py-3 text-gray-400">
            <Loader2 size={16} className="animate-spin" />
          </div>
        ) : tenants.length > 1 ? (
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">
              {t('auth.selectSchool')}
              <span className="text-red-500 ms-0.5">*</span>
            </label>
            <div className="relative">
              <School size={16} className="absolute start-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
                required
                className="block w-full rounded-lg border border-gray-300 ps-10 pe-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-offset-0
                  focus:border-primary-500 focus:ring-primary-200 bg-white
                  appearance-none"
              >
                <option value="">{t('auth.chooseSchool')}</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>{tenant.name_ar} — {tenant.name_en}</option>
                ))}
              </select>
            </div>
          </div>
        ) : null}

        <Input
          label={t('auth.username')}
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={t('auth.username')}
          required
          autoFocus={tenants.length <= 1}
        />

        <Input
          label={t('auth.password')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t('auth.password')}
          required
        />

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-600">
              {t('auth.rememberMe')}
            </span>
          </label>

          <Link
            to="/forgot-password"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {t('auth.forgotPassword')}
          </Link>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          loading={isLoading}
          disabled={tenants.length > 1 && !selectedTenant}
        >
          {t('auth.login')}
        </Button>
      </form>
    </div>
  );
}
